import { useState, useEffect } from 'react';
import { Users, Search, ChevronRight } from 'lucide-react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Organization, OrganizationMember } from '../../types/index';
import { Link } from 'react-router-dom';
import { useAuthReady } from '../../hooks/useAuthReady';

export function OrganizationsList() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authReady = useAuthReady(true);

  useEffect(() => {
    // Wait for auth to be ready before fetching
    if (!authReady) {
      console.log('⏳ Waiting for auth to be ready before fetching organizations...');
      return;
    }

    const fetchOrganizations = async () => {
      try {
        console.log('📋 Fetching organizations...');
        const organizationsQuery = query(collection(db, 'organizations'));
        const snapshot = await getDocs(organizationsQuery);
        const orgs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Organization));
        setOrganizations(orgs);
        console.log(`✅ Loaded ${orgs.length} organizations`);
      } catch (error) {
        console.error('❌ Error fetching organizations:', error);
        setError('Failed to load organizations');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizations();
  }, [authReady]);

  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.members.some((member: OrganizationMember) => 
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-menioo-green"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4 text-center">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center space-x-3">
          <Users className="h-6 w-6 text-menioo-green" />
          <h2 className="text-xl font-display font-bold text-menioo-navy">
            Organizations
          </h2>
          <Link
            to="/admin/organizations/admin"
            className="text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Organisation Admin →
          </Link>
        </div>

        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-menioo-green focus:border-menioo-green"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Members
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{org.name}</div>
                    <div className="text-sm text-gray-500">ID: {org.id}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{org.members.length} members</div>
                    <div className="text-sm text-gray-500">
                      {org.members.find((m: OrganizationMember) => m.userId === org.creatorId)?.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <Link
                      to={`/admin/organizations/${org.id}`}
                      className="inline-flex items-center text-menioo-green hover:text-menioo-green-dark"
                    >
                      Manage
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}